/**
 * Landing-page conversion enhancers — three "waouh" effects:
 *   1. QuickstartForm : 2-field inline form replacing the hero CTA.
 *      → POST /api/studio/quickstart (anonymous) → /studio/generating/:id?mode=demo
 *   3. HeroCounter : "X PS make confiance" counter with countUp animation on viewport entry.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Stethoscope, Eye, Building2, Wand2, ArrowRight, Zap, MapPin } from "lucide-react";
import { API } from "../../apiBase";

const SPECIALTIES = [
  { id: "Médecin", label: "Médecin", icon: "👨‍⚕️" },
  { id: "Infirmier", label: "Infirmier", icon: "💉" },
  { id: "Kiné", label: "Kiné", icon: "🦴" },
  { id: "Ophtalmo", label: "Ophtalmo", icon: "👁️" },
  { id: "Dermato", label: "Dermato", icon: "🔬" },
  { id: "CPTS", label: "CPTS", icon: "🏥" },
];

// ============================================================================
// QuickstartForm
// ============================================================================
export function QuickstartForm() {
  const [brand, setBrand] = useState("");
  const [specialty, setSpecialty] = useState("Médecin");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  const canSubmit = brand.trim().length >= 2 && !submitting;

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (!canSubmit) return;
    setSubmitting(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/studio/quickstart`, {
        brand_name: brand.trim(),
        specialite: specialty,
      });
      navigate(`/studio/generating/${data.id}?mode=demo&specialite=${encodeURIComponent(specialty)}&name=${encodeURIComponent(brand.trim())}`);
    } catch (err) {
      setError(err?.response?.data?.detail || "Impossible de générer votre aperçu — réessayez dans un instant.");
      setSubmitting(false);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      data-testid="quickstart-form"
      className="relative mx-auto mt-9 max-w-xl p-5 sm:p-6 fade-up delay-2 text-left"
      style={{
        background: "#ffffff",
        border: "0.5px solid #BAE6FD",
        borderRadius: 16,
        boxShadow: "0 20px 48px -20px rgba(15,43,91,0.12)",
      }}
    >
      <div className="flex items-center gap-2 mb-1">
        <Zap size={14} style={{ color: "#4FC3F7" }} />
        <span className="text-[11px] uppercase font-semibold" style={{ color: "#4FC3F7", letterSpacing: "0.08em" }}>
          30 secondes · sans inscription · gratuit
        </span>
      </div>
      <h3 className="font-display text-[19px] sm:text-[22px] leading-snug" style={{ color: "#0F2B5B", fontWeight: 500 }}>
        Votre plateforme de télémédecine en aperçu live
      </h3>

      <label className="block mt-4">
        <span className="text-[11.5px] uppercase tracking-wider font-medium" style={{ color: "#94A3B8" }}>Nom de votre cabinet</span>
        <input
          autoFocus
          value={brand}
          onChange={(e) => setBrand(e.target.value)}
          placeholder="Ex. Cabinet du Centre, MSP Bel Air, CPTS Loire…"
          data-testid="quickstart-brand"
          maxLength={120}
          className="mt-1 w-full px-3.5 py-2.5 text-[14px] outline-none transition-colors"
          style={{
            background: "#F8FAFC",
            border: "0.5px solid #CBD5E0",
            borderRadius: 8,
            color: "#0F2B5B",
          }}
          onFocus={(e) => { e.currentTarget.style.borderColor = "#4FC3F7"; e.currentTarget.style.boxShadow = "0 0 0 3px rgba(79,195,247,0.15)"; }}
          onBlur={(e) => { e.currentTarget.style.borderColor = "#CBD5E0"; e.currentTarget.style.boxShadow = "none"; }}
        />
      </label>

      <div className="mt-4">
        <div className="text-[11.5px] uppercase tracking-wider font-medium mb-2" style={{ color: "#94A3B8" }}>Vous êtes</div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {SPECIALTIES.map((s) => {
            const active = specialty === s.id;
            return (
              <button
                type="button"
                key={s.id}
                onClick={() => setSpecialty(s.id)}
                data-testid={`quickstart-spec-${s.id}`}
                className="flex items-center justify-center gap-1.5 px-3 py-2 text-[12.5px] font-medium transition-colors"
                style={{
                  background: active ? "#0F2B5B" : "#F8FAFC",
                  color: active ? "#ffffff" : "#64748B",
                  border: `0.5px solid ${active ? "#0F2B5B" : "#CBD5E0"}`,
                  borderRadius: 999,
                  boxShadow: active ? "0 4px 12px -4px rgba(15,43,91,0.30)" : "none",
                }}
              >
                <span className="text-[14px] leading-none">{s.icon}</span>
                <span>{s.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      <button
        type="submit"
        disabled={!canSubmit}
        data-testid="quickstart-submit"
        className="mt-5 w-full px-5 py-3.5 text-[14.5px] font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
        style={{
          background: "#0F2B5B",
          color: "#ffffff",
          border: 0,
          borderRadius: 999,
          boxShadow: "0 12px 28px -10px rgba(15,43,91,0.45)",
        }}
      >
        {submitting ? "Génération en cours…" : (<><Wand2 size={15} /> Générer mon aperçu gratuit <ArrowRight size={15} /></>)}
      </button>

      {error && <div className="mt-2 text-[12px]" style={{ color: "#DC2626" }} data-testid="quickstart-error">{error}</div>}

      <p className="mt-3 text-[11.5px] text-center" style={{ color: "#94A3B8" }}>
        Aucune carte bancaire · Pas d&apos;email demandé · Sandbox prête en moins de 10 secondes
      </p>
    </form>
  );
}


// ============================================================================
// HeroCounter — animated count-up on viewport entry
// ============================================================================
export function HeroCounter() {
  const [stats, setStats] = useState({ total_pros: 0, weekly_new: 0 });
  const [count, setCount] = useState(0);
  const [animated, setAnimated] = useState(false);
  const elRef = useRef(null);

  useEffect(() => {
    let alive = true;
    const load = () => axios.get(`${API}/studio/landing-stats`).then((r) => alive && setStats(r.data)).catch(() => {});
    load();
    const id = setInterval(load, 5 * 60 * 1000);
    return () => { alive = false; clearInterval(id); };
  }, []);

  useEffect(() => {
    if (!stats.total_pros || animated) return;
    const obs = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) {
          setAnimated(true);
          obs.disconnect();
        }
      });
    }, { threshold: 0.4 });
    if (elRef.current) obs.observe(elRef.current);
    return () => obs.disconnect();
  }, [stats.total_pros, animated]);

  useEffect(() => {
    if (!animated) return;
    const start = performance.now();
    const dur = 1500;
    const target = stats.total_pros;
    let raf;
    const tick = (t) => {
      const p = Math.min(1, (t - start) / dur);
      // ease-out cubic
      const eased = 1 - Math.pow(1 - p, 3);
      setCount(Math.round(eased * target));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [animated, stats.total_pros]);

  return (
    <div
      ref={elRef}
      data-testid="hero-counter"
      className="mt-4 inline-flex items-center gap-2 text-[13px] fade-up delay-2"
      style={{ color: "#0F2B5B" }}
    >
      <Stethoscope size={14} style={{ color: "#4FC3F7" }} />
      <span>
        <span className="font-bold tabular-nums" data-testid="hero-counter-value" style={{ color: "#0F2B5B" }}>{count}</span>
        {" "}professionnels de santé font confiance à MetaConnect
      </span>
      {stats.weekly_new > 0 && (
        <span className="hidden sm:inline-flex items-center gap-1 text-[11.5px] ml-2 pl-2" style={{ color: "#4FC3F7", borderLeft: "1px solid #E2E8F0", fontWeight: 500 }}>
          <MapPin size={11} /> +{stats.weekly_new} cette semaine
        </span>
      )}
    </div>
  );
}
